<template>
  <div class="contactsTable">
    <!-- Filters -->
    <div class="filters">
      <!-- Account SearchSelect: hidden when table is already scoped to a single account -->
      <SearchSelect
        v-if="!accountId"
        v-model="filterAccountId"
        :options="accountOptions"
        placeholder="All accounts"
        searchPlaceholder="Search accounts…"
        class="filterAccountSelect"
        @update:modelValue="onFilterChange"
      />

      <!-- Enrolled after date -->
      <div class="filterGroup">
        <label class="filterLabel">Enrolled after</label>
        <input
          class="input filterDate"
          type="date"
          v-model="filterEnrolledAfter"
          @change="onFilterChange"
        />
      </div>

      <!-- Time in sequence range -->
      <div class="filterGroup">
        <label class="filterLabel">Days in sequence</label>
        <div class="rangeInputs">
          <input
            class="input filterDays"
            type="number"
            min="0"
            placeholder="Min"
            v-model.number="filterMinDays"
            @change="onFilterChange"
          />
          <span class="rangeSep">–</span>
          <input
            class="input filterDays"
            type="number"
            min="0"
            placeholder="Max"
            v-model.number="filterMaxDays"
            @change="onFilterChange"
          />
        </div>
      </div>

      <!-- Replied filter: vertically aligned with other filter controls -->
      <div class="filterGroup">
        <label class="filterLabel">Replied</label>
        <div class="filterToggleWrap">
          <ToggleSwitch
            v-model="filterReplied"
            label="Only"
            @update:modelValue="onFilterChange"
          />
        </div>
      </div>

      <!-- Pending Approval filter -->
      <div class="filterGroup">
        <label class="filterLabel">Pending Approval</label>
        <div class="filterToggleWrap">
          <ToggleSwitch
            v-model="filterPendingApproval"
            label="Only"
            @update:modelValue="onFilterChange"
          />
        </div>
      </div>
    </div>

    <!-- Active filter pill from funnel clicks -->
    <div v-if="stepFilter" class="activeFilterRow">
      <span class="filterPill">
        {{ stepFilterLabel }}
        <button class="clearFilter" @click="emit('clear-filter')" title="Clear filter">×</button>
      </span>
    </div>

    <!-- Table -->
    <div class="tableWrap">
      <div class="tableScroll">
      <div class="tableHeader">
        <div class="col-contact">Contact</div>
        <div class="col-account">Account</div>
        <div class="col-status">Status</div>
        <div class="col-step">Step</div>
        <div class="col-enrolled">Enrolled</div>
        <div class="col-duration">Time in Seq</div>
        <div class="col-exit">Exit Reason</div>
      </div>

      <div v-if="loading && rows.length === 0" class="tableEmpty">Loading…</div>
      <div v-else-if="error" class="tableError">{{ error }}</div>
      <div v-else-if="rows.length === 0 && !loading" class="tableEmpty">No contacts match the current filters.</div>

      <template v-else>
        <div v-for="row in rows" :key="row.id">
          <!-- Main row -->
          <div
            class="tableRow"
            :class="{ expanded: expandedId === row.id }"
            role="button"
            tabindex="0"
            @click="toggleExpand(row)"
            @keydown.enter.prevent="toggleExpand(row)"
          >
            <div class="col-contact">
              <span class="expandChevron" :class="{ open: expandedId === row.id }">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="12" height="12">
                  <polyline points="4,6 8,10 12,6"/>
                </svg>
              </span>
              <span class="contactName">{{ row.contact_name || '—' }}</span>
            </div>
            <div class="col-account">
              <FavIcon v-if="row.account_domain" :domain="row.account_domain" :size="14" class="accountFavicon" />
              <span class="accountName">{{ row.account_name || '—' }}</span>
            </div>
            <div class="col-status">
              <span class="statusBadge" :class="statusBadgeClass(row)">{{ statusLabel(row.status, row.exit_reason) }}</span>
            </div>
            <div class="col-step">
              <span v-if="(row.status === 'active' || row.status === 'paused') && row.needs_exit_decision" class="awaitingExit">Awaiting Exit</span>
              <span v-else-if="row.status === 'active' || row.status === 'paused'">
                {{ stepName(row.current_step_index) }}
              </span>
              <span v-else class="muted">—</span>
            </div>
            <div class="col-enrolled">{{ fmtDate(row.enrolled_at) }}</div>
            <div class="col-duration">
              <span v-if="timeInSequence(row) !== null" class="daysVal">{{ timeInSequence(row) }}</span>
              <span v-else class="muted">—</span>
            </div>
            <div class="col-exit">
              <span v-if="row.exit_reason" class="exitReason">{{ exitLabel(row.status, row.exit_reason) }}</span>
              <span v-else class="muted">—</span>
            </div>
          </div>

          <!-- Expanded detail -->
          <div v-if="expandedId === row.id" class="expandedRow">
            <SequenceEnrollmentDetail
              :enrollmentId="row.id"
              :sequenceSteps="steps"
              @reachout-click="onReachoutClick"
            />
          </div>
        </div>

        <!-- Infinite scroll sentinel -->
        <div v-if="hasMore" ref="sentinel" class="loadMore">
          <span v-if="loadingMore">Loading more…</span>
        </div>
      </template>
      </div><!-- end tableScroll -->
    </div>

    <div v-if="total > 0" class="tableFooter">
      {{ rows.length }} of {{ total }} contacts
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import SequenceEnrollmentDetail from './SequenceEnrollmentDetail.vue'
import SearchSelect from './SearchSelect.vue'
import ToggleSwitch from './ToggleSwitch.vue'
import FavIcon from './FavIcon.vue'
import { apiFetch } from '../services/api.ts'
import { sequenceExitLabel } from '../services/sequenceUtils.ts'

const props = defineProps({
  sequenceId: { type: String, required: true },
  accountId: { type: String, default: null },  // lock table to a single account
  steps: { type: Array, default: () => [] },
  // stepFilter: driven by funnel clicks on detail page
  // { type: 'step', index: N } | { type: 'exit', exitType: 'completed'|'timed_out'|'removed' } | null
  stepFilter: { type: Object, default: null },
  // Accounts that participated in this sequence (for SearchSelect)
  participantAccounts: { type: Array, default: () => [] },
  pollTick: { type: Number, default: 0 },
})

const emit = defineEmits(['total', 'clear-filter', 'reachout-click'])

const rows = ref([])
const total = ref(0)
const hasMore = ref(false)
const loading = ref(false)
const loadingMore = ref(false)
const error = ref('')
const expandedId = ref(null)

const filterAccountId = ref(props.accountId || '')
const filterEnrolledAfter = ref('')
const filterMinDays = ref(null)
const filterMaxDays = ref(null)
const filterReplied = ref(false)
const filterPendingApproval = ref(false)

const sentinel = ref(null)
let observer = null
const LIMIT = 50

// Build SearchSelect options from participantAccounts prop
const accountOptions = computed(() =>
  props.participantAccounts.map(a => ({
    value: String(a.id),
    label: a.name || String(a.id).slice(0, 8),
    domain: a.domain || '',
  }))
)

// Build step options from steps prop (non-wait steps only)
const stepFilterLabel = computed(() => {
  const f = props.stepFilter
  if (!f) return ''
  if (f.type === 'active') return 'Active'
  if (f.type === 'step') {
    const s = props.steps.find(s => s.step_index === f.index)
    const typeName = s ? stepTypeName(s.step_type) : `Step ${f.index + 1}`
    return `Step ${f.index + 1}: ${typeName}`
  }
  if (f.type === 'exit') {
    const m = { completed: 'Engaged', timed_out: 'Timed Out', removed: 'Disqualified / Removed', pending_exit: 'Awaiting Exit' }
    return m[f.exitType] || f.exitType
  }
  return ''
})

function stepTypeName(type) {
  const m = { email: 'Email', linkedin_connect: 'LinkedIn', voicemail: 'Voicemail', wait: 'Wait' }
  return m[type] || type
}

function stepName(idx) {
  const s = props.steps.find(s => s.step_index === idx)
  if (!s) return `Step ${idx + 1}`
  return `Step ${idx + 1}: ${stepTypeName(s.step_type)}`
}

function statusBadgeClass(row) {
  if (row.status === 'completed' && row.exit_reason === 'disqualified') return 'status-disqualified'
  if (row.status === 'completed' && row.exit_reason && row.exit_reason !== 'engaged') return 'status-exited'
  return `status-${row.status}`
}

function statusLabel(status, exitReason) {
  if (status === 'active') return 'Active'
  if (status === 'paused') return 'Paused'
  if (status === 'completed') return exitLabel(status, exitReason) || 'Completed'
  return status
}

function exitLabel(status, reason) {
  return sequenceExitLabel(status, reason)
}

function fmtDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function timeInSequence(row) {
  if (!row.enrolled_at) return null
  const end = row.status === 'completed' && row.updated_at
    ? new Date(row.updated_at)
    : new Date()
  const ms = end - new Date(row.enrolled_at)
  if (ms < 0) return null
  const totalMinutes = Math.round(ms / 60000)
  if (totalMinutes < 60) return `${totalMinutes}m`
  const totalHours = Math.round(ms / 3600000)
  if (totalHours < 24) return `${totalHours}h`
  return `${Math.round(ms / 86400000)}d`
}

function buildParams(offset = 0) {
  const params = new URLSearchParams()
  params.set('offset', String(offset))
  params.set('limit', String(LIMIT))

  // Step filter from funnel (takes precedence over free-form filters)
  const f = props.stepFilter
  if (f) {
    if (f.type === 'active') {
      params.set('status', 'active,paused')
    } else if (f.type === 'step') {
      params.set('status', 'active,paused')
      params.set('step_index', String(f.index))
    } else if (f.type === 'exit') {
      if (f.exitType === 'pending_exit') {
        params.set('status', 'active,paused')
        params.set('step_index', '-1')
      } else if (f.exitType === 'completed') {
        params.set('status', 'completed')
        params.set('exit_reason', 'engaged')
      } else if (f.exitType === 'timed_out') {
        params.set('status', 'completed')
        params.set('exit_reason', 'timed_out')
      } else if (f.exitType === 'removed') {
        params.set('status', 'completed')
        params.set('exit_reason', 'other')
      }
    }
  }

  // Account filter
  const accountFilter = props.accountId || filterAccountId.value || null
  if (accountFilter) params.set('account_id', accountFilter)

  if (filterEnrolledAfter.value) {
    params.set('enrolled_after', new Date(filterEnrolledAfter.value).toISOString())
  }

  if (filterReplied.value) {
    params.set('has_reply', 'true')
  }

  if (filterPendingApproval.value) {
    params.set('has_pending_approval', 'true')
  }

  if (filterMinDays.value != null && filterMinDays.value !== '' && filterMinDays.value >= 0) {
    params.set('min_days_in_sequence', String(filterMinDays.value))
  }
  if (filterMaxDays.value != null && filterMaxDays.value !== '' && filterMaxDays.value >= 0) {
    params.set('max_days_in_sequence', String(filterMaxDays.value))
  }

  return params
}

async function load() {
  loading.value = true
  error.value = ''
  rows.value = []
  try {
    const params = buildParams(0)
    const resp = await apiFetch(`/sequences/${encodeURIComponent(props.sequenceId)}/enrollments?${params}`)
    rows.value = resp.items || []
    total.value = resp.total || 0
    hasMore.value = resp.has_more || false
    emit('total', total.value)
  } catch (e) {
    error.value = e?.message || 'Failed to load contacts'
  } finally {
    loading.value = false
  }
}

async function loadMore() {
  if (loadingMore.value || !hasMore.value) return
  loadingMore.value = true
  try {
    const params = buildParams(rows.value.length)
    const resp = await apiFetch(`/sequences/${encodeURIComponent(props.sequenceId)}/enrollments?${params}`)
    rows.value = [...rows.value, ...(resp.items || [])]
    hasMore.value = resp.has_more || false
  } catch {
    // silent on infinite scroll failure
  } finally {
    loadingMore.value = false
  }
}

function onFilterChange() {
  expandedId.value = null
  load()
}

function toggleExpand(row) {
  expandedId.value = expandedId.value === row.id ? null : row.id
}

function onReachoutClick(reachoutId) {
  emit('reachout-click', reachoutId)
}

function setupObserver() {
  if (observer) observer.disconnect()
  observer = new IntersectionObserver((entries) => {
    if (entries[0]?.isIntersecting && hasMore.value && !loadingMore.value) {
      loadMore()
    }
  }, { rootMargin: '100px' })
  if (sentinel.value) observer.observe(sentinel.value)
}

watch(sentinel, (el) => {
  if (el && observer) observer.observe(el)
})

// Silent poll refresh — re-fetch current visible rows without resetting UI
async function silentReload() {
  try {
    const currentCount = Math.max(rows.value.length, LIMIT)
    const params = buildParams(0)
    params.set('limit', String(currentCount))
    const resp = await apiFetch(`/sequences/${encodeURIComponent(props.sequenceId)}/enrollments?${params}`)
    rows.value = resp.items || []
    total.value = resp.total || 0
    hasMore.value = resp.has_more || false
    emit('total', total.value)
  } catch {
    // silent — don't disrupt display on poll failure
  }
}

watch(() => props.pollTick, silentReload)

watch(() => props.stepFilter, () => {
  expandedId.value = null
  load()
}, { deep: true })

watch(() => props.sequenceId, load)

onMounted(() => {
  load()
  setupObserver()
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})
</script>

<style scoped>
.contactsTable {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: flex-end;
  margin-bottom: 12px;
}

.filterAccountSelect {
  min-width: 200px;
  max-width: 280px;
}

.filterToggleWrap {
  display: flex;
  align-items: center;
  height: 34px; /* match input height so it aligns with other filter controls */
}

.filterGroup {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filterLabel {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--muted);
}

.filterDate {
  min-width: 140px;
}

.rangeInputs {
  display: flex;
  align-items: center;
  gap: 4px;
}

.filterDays {
  width: 70px;
}

.rangeSep {
  font-size: 13px;
  color: var(--muted);
}

.activeFilterRow {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
}

.filterPill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 8px 3px 10px;
  border-radius: 10px;
  background: color-mix(in srgb, var(--primary) 12%, var(--surface));
  color: var(--primary);
  border: 1px solid color-mix(in srgb, var(--primary) 30%, var(--border));
}

.clearFilter {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: var(--primary);
  font-size: 16px;
  line-height: 1;
  display: flex;
  align-items: center;
}

.tableWrap {
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
}

.tableScroll {
  overflow-x: auto;
  min-width: 0;
}

.tableHeader {
  display: grid;
  grid-template-columns: minmax(130px,1.4fr) minmax(120px,1.4fr) 90px 120px 95px 80px 100px;
  gap: 10px;
  padding: 8px 14px;
  background: var(--surface2, var(--surface));
  border-bottom: 1px solid var(--border);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--muted);
}

.tableRow {
  display: grid;
  grid-template-columns: minmax(130px,1.4fr) minmax(120px,1.4fr) 90px 120px 95px 80px 100px;
  gap: 10px;
  padding: 10px 14px;
  border-bottom: 1px solid var(--border2);
  cursor: pointer;
  align-items: center;
  font-size: 13px;
}

.tableRow:hover,
.tableRow.expanded {
  background: var(--row-hover-bg);
}

.tableRow:last-child {
  border-bottom: none;
}

.tableRow:focus-visible {
  outline: 2px solid var(--focus-border);
  outline-offset: -2px;
}

.expandedRow {
  padding: 0 14px 4px 14px;
  border-bottom: 1px solid var(--border2);
  background: var(--bg);
}

.tableEmpty,
.tableError {
  padding: 16px;
  font-size: 13px;
  color: var(--muted);
  text-align: center;
}

.tableError { color: var(--danger); }

.expandChevron {
  display: inline-flex;
  align-items: center;
  color: var(--muted);
  transition: transform 0.15s;
  margin-right: 6px;
  flex-shrink: 0;
}

.expandChevron.open {
  transform: rotate(180deg);
}

.col-contact {
  display: flex;
  align-items: center;
  min-width: 0;
  overflow: hidden;
}

.contactName {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}

.col-account {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  overflow: hidden;
}

.accountFavicon {
  flex-shrink: 0;
  border-radius: 2px;
}

.accountName {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.col-step,
.col-enrolled,
.col-duration,
.col-exit {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--muted);
  font-size: 12px;
}

.daysVal {
  font-size: 12px;
  color: var(--text);
}

.statusBadge {
  font-size: 11px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 4px;
  background: var(--surface2, var(--surface));
  color: var(--muted);
  border: 1px solid var(--border);
  white-space: nowrap;
}

.status-active { color: var(--primary); border-color: var(--primary); background: color-mix(in srgb, var(--primary) 8%, transparent); }
.status-paused { color: var(--warning, #f59e0b); border-color: var(--warning, #f59e0b); background: color-mix(in srgb, var(--warning, #f59e0b) 8%, transparent); }
.status-completed { color: var(--success); border-color: var(--success); background: color-mix(in srgb, var(--success) 8%, transparent); }
.status-disqualified { color: var(--danger); border-color: var(--danger); background: color-mix(in srgb, var(--danger) 8%, transparent); }

.awaitingExit {
  font-size: 12px;
  font-weight: 600;
  color: var(--warning);
}

.exitReason {
  font-size: 12px;
  color: var(--muted);
}

.muted { color: var(--muted); }

.loadMore {
  padding: 12px;
  text-align: center;
  font-size: 13px;
  color: var(--muted);
}

.tableFooter {
  margin-top: 8px;
  font-size: 12px;
  color: var(--muted);
  text-align: right;
}
</style>
